import { normaliseEmailArray } from "src/utils";
import { UpdateVendorFormData, PatchVendorRequestBody, Vendor } from "src/types";
import { VendorFileType } from "src/enums/vendorFileType";
import { CollectionType } from "src/enums/collectionType";
export class UpdateVendorMapper {
  public static initUpdateVendorFormData(): UpdateVendorFormData {
    return {
      vendorName: "",
      editableVendorName: "",
      emails: [""],
      location: "",
      portalOrderVendor: false,
      collectionOrderVendor: false,
      collectionType: CollectionType.BRAND_SHIPS, // Default to "Brand Ships" when collectionOrderVendor is false
      isProForma: false,
      emailGreeting: "",
      portalAccess: false,
      fileType: VendorFileType.PDF,
      invoiceName: "",
      paymentTerms: "",
      incoTerms: "" as const,
      countryOfExport: [],
      shipmentDetails: {
        opsPOC: "",
        notes: "",
        ein: "",
        collection: "",
        ciRequired: false,
        bolRequired: false,
        addresses: [],
        contactPerson: "",
        contactInfo: "",
        email: ""
      },
      isDirectToAA: false
    };
  }

  public static initUpdateVendorFormDataFromVendor(vendor: Vendor): UpdateVendorFormData {
    // Determine default collectionType based on collectionOrderVendor
    let defaultCollectionType: string;
    if (!vendor.collectionOrderVendor) {
      // If collectionOrderVendor is false, always use "Brand Ships"
      defaultCollectionType = CollectionType.BRAND_SHIPS;
    } else {
      // If collectionOrderVendor is true, use existing collectionType or default to "ParcelHero"
      defaultCollectionType = vendor.collectionType || CollectionType.PARCEL_HERO;
    }

    return {
      vendorName: vendor.name,
      editableVendorName: vendor.name,
      emails: vendor.emails && vendor.emails.length > 0 ? vendor.emails : [""],
      location: vendor.location,
      portalOrderVendor: vendor.portalOrderVendor ?? false,
      collectionOrderVendor: vendor.collectionOrderVendor ?? false,
      collectionType: defaultCollectionType,
      isProForma: vendor.isProForma ?? false,
      emailGreeting: vendor.emailGreeting || "",
      portalAccess: vendor.portalAccess ?? false,
      fileType: vendor.fileType ?? VendorFileType.PDF,
      invoiceName: vendor.invoiceName || "",
      paymentTerms: vendor.paymentTerms || "",
      incoTerms: vendor.incoTerms ?? "",
      countryOfExport: vendor.countryOfExport && vendor.countryOfExport.length > 0 ? vendor.countryOfExport : [],
      shipmentDetails: vendor.shipmentDetails || {
        opsPOC: "",
        notes: "",
        ein: "",
        collection: "",
        ciRequired: false,
        bolRequired: false,
        addresses: [],
        contactPerson: "",
        contactInfo: "",
        email: ""
      },
      isDirectToAA: vendor.isDirectToAA ?? false
    };
  }

  public static updateVendorRequestBodyFromFormData(
    formData: UpdateVendorFormData,
    initialFormData?: UpdateVendorFormData
  ): PatchVendorRequestBody {
    const normalisedEmails = normaliseEmailArray(formData.emails);
    const normalisedName =
      formData.editableVendorName === initialFormData?.editableVendorName || ""
        ? undefined
        : formData.editableVendorName;
    const normalisedLocation = formData.location === initialFormData?.location || "" ? undefined : formData.location;
    const normalisedEmailGreeting =
      formData.emailGreeting === initialFormData?.emailGreeting || "" ? undefined : formData.emailGreeting || null;
    const normalisedInvoiceName =
      formData.invoiceName === initialFormData?.invoiceName || "" ? undefined : formData.invoiceName?.trim() || null;
    const normalisedPaymentTerms =
      formData.paymentTerms === initialFormData?.paymentTerms || "" ? undefined : formData.paymentTerms?.trim() || null;
    const normalisedIncoTerms =
      formData.incoTerms === initialFormData?.incoTerms ? undefined : formData.incoTerms || null;

    // Normalize countryOfExport: compare arrays, if same or both empty, set to undefined
    const initialCountryOfExport = initialFormData?.countryOfExport || [];
    const currentCountryOfExport = formData.countryOfExport || [];
    const arraysEqual =
      initialCountryOfExport.length === currentCountryOfExport.length &&
      initialCountryOfExport.every((val, idx) => val === currentCountryOfExport[idx]);
    const normalisedCountryOfExport = arraysEqual
      ? undefined
      : currentCountryOfExport.length === 0
        ? null
        : currentCountryOfExport;

    let normalisedShipmentDetails = undefined;
    if (JSON.stringify(formData.shipmentDetails) !== JSON.stringify(initialFormData?.shipmentDetails)) {
      normalisedShipmentDetails = {
        ...formData.shipmentDetails
      };
    }

    const normalisedCollectionType =
      formData.collectionType !== initialFormData?.collectionType ? formData.collectionType || null : undefined;

    return {
      name: normalisedName,
      emails: normalisedEmails,
      location: normalisedLocation,
      portalOrderVendor:
        formData.portalOrderVendor !== initialFormData?.portalOrderVendor ? formData.portalOrderVendor : undefined,
      collectionOrderVendor:
        formData.collectionOrderVendor !== initialFormData?.collectionOrderVendor
          ? formData.collectionOrderVendor
          : undefined,
      collectionType: normalisedCollectionType,
      isProForma: formData.isProForma !== initialFormData?.isProForma ? formData.isProForma : undefined,
      emailGreeting: normalisedEmailGreeting,
      portalAccess: formData.portalAccess !== initialFormData?.portalAccess ? formData.portalAccess : undefined,
      fileType: formData.fileType !== initialFormData?.fileType ? formData.fileType : undefined,
      invoiceName: normalisedInvoiceName,
      paymentTerms: normalisedPaymentTerms,
      incoTerms: normalisedIncoTerms,
      countryOfExport: normalisedCountryOfExport,
      shipmentDetails: normalisedShipmentDetails,
      isDirectToAA: formData.isDirectToAA !== initialFormData?.isDirectToAA ? formData.isDirectToAA : undefined
    };
  }
}
